import React from 'react';
import Cookies from 'js-cookie';
import jwtDecode from 'jwt-decode';

import { COOKIES } from '../constants';
import { LoginToken } from '@/types/token';
import { GalleryCollection, InventoryCollection, MergedCollection } from '@/types/collection';
import {
  GallerySingleDrop,
  InventorySingleDrop,
  MergedSingleDrop,
  PromoDrop,
} from '@/types/singleDrop';
import { dropsApi } from '@/lib/api';

export const isAuthenticated = () => {
  const token = Cookies.get(COOKIES.ACCESS_TOKEN);

  if (token) {
    const data = jwtDecode<LoginToken>(token);
    const isTokenValid = data !== null && data.exp > Date.now() / 1000;

    return isTokenValid;
  }

  return false;
};

export const validateToken = (token?: string) => {
  if (!token) {
    return false;
  }

  const data = jwtDecode<LoginToken>(token);
  return data.exp > Date.now() / 1000;
};

export const accessToken = {
  get: () => Cookies.get(COOKIES.ACCESS_TOKEN),
  set: (token: string) => Cookies.set(COOKIES.ACCESS_TOKEN, token),
  remove: () => Cookies.remove(COOKIES.ACCESS_TOKEN),
};

export const getTokenData = () => {
  const token = accessToken.get();

  if (token) {
    return jwtDecode<LoginToken>(token);
  }

  return undefined;
};

export const generateLoginUrl = (origin: string, payload: Record<string, string | number>) => {
  const projectId = process.env.NEXT_PUBLIC_LOGIN_WIDGET_PROJECT_ID || '';
  const loginUrl = process.env.NEXT_PUBLIC_LOGIN_URL || '';

  const urlParams = new URLSearchParams({
    projectId,
    login_url: origin,
    fields: 'email',
    with_logout: '1',
  });

  if (payload) {
    urlParams.set('payload', JSON.stringify(payload));
  }

  return `${loginUrl}/social/babka/login_redirect?${urlParams.toString()}`;
};

export function mergeCollection(
  galleryCollections: GalleryCollection[],
  inventoryCollections: InventoryCollection[],
): MergedCollection[] {
  return galleryCollections.map<MergedCollection>(
    (collection) => {
      const claimedCollection = inventoryCollections.find(
        (item) => collection.id === item.id,
      );

      if (claimedCollection) {
        return {
          ...collection,
          ...claimedCollection,
          isClaimed: true,
        };
      }

      return { ...collection, isClaimed: false };
    },
  );
}

export async function updatePromoDrops(promoDrops: PromoDrop[]) {
  const newPromoArr: PromoDrop[] = [];

  const res = await Promise.all(promoDrops.map((drop) => dropsApi.getDropClaimInfo(drop.slug)));

  res.forEach(({ date_claim, id }, index) => (
    newPromoArr.push({ ...promoDrops[index], id, isClaimed: !!date_claim })
  ));

  return newPromoArr;
}

export function mergeSingleDrops(
  gallerySingleDrops: GallerySingleDrop[],
  inventorySingleDrops: InventorySingleDrop[],
): MergedSingleDrop[] {
  return gallerySingleDrops.map<MergedSingleDrop>((gallerySingleDrop) => {
    const inventorySingleDrop = inventorySingleDrops.find(
      (item) => item.id === gallerySingleDrop.id,
    );

    if (inventorySingleDrop) {
      return { ...gallerySingleDrop, ...inventorySingleDrop };
    }

    return gallerySingleDrop;
  });
}

export function isVideoLink(link?: string): boolean {
  if (!link) {
    return false;
  }

  const supportedVideoFormats = ['mp4', 'mov', 'webm', 'mp3', 'avi', 'ogg'];
  const assetUrl = new URL(link);
  const extension = assetUrl.pathname.split('.')[1];

  return supportedVideoFormats.includes(extension);
}

export function toggleVideo(videoRef: React.RefObject<HTMLVideoElement>, play: boolean) {
  if (videoRef.current) {
    if (play) {
      videoRef.current.play();
    } else {
      videoRef.current.pause();
    }
  }
}
